import { useCallback } from "react";
import { useDispatch } from "react-redux";

import notification from "antd/es/notification";
import { defineMessages, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { EntityType } from "@mapmycustomers/shared/enum";

import Path from "@app/enum/Path";
import RouteSubPath from "@app/enum/RouteSubPath";
import { showEntityView } from "@app/store/entityView/actions";

import { checkEntityMentionAccess } from "../store/actions";

import { GROUP_TO_ENTITY } from "./entityNavigationTargets";

const messages = defineMessages({
  groupNoAccess: {
    id: "entityMentions.navigate.groupNoAccess",
    defaultMessage: "You don't have access to this group, or it has been deleted.",
    description:
      "Notification shown when a mention pill targets a group the user can't read or that no longer exists",
  },
  routeNoAccess: {
    id: "entityMentions.navigate.routeNoAccess",
    defaultMessage: "You don't have access to this route, or it has been deleted.",
    description:
      "Notification shown when a mention pill targets a route the user can't read or that no longer exists",
  },
  territoryNoAccess: {
    id: "entityMentions.navigate.territoryNoAccess",
    defaultMessage: "You don't have access to this territory, or it has been deleted.",
    description:
      "Notification shown when a mention pill targets a territory the user can't read or that no longer exists",
  },
});

const useEntityMentionNavigation = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();

  return useCallback(
    (entityType: EntityType, entityId: number) => {
      if ([EntityType.COMPANY, EntityType.DEAL, EntityType.PERSON].includes(entityType)) {
        dispatch(showEntityView({ entityId, entityType }));
        return;
      }
      const groupTarget = GROUP_TO_ENTITY[entityType];
      if (groupTarget) {
        dispatch(
          checkEntityMentionAccess({
            entityId,
            entityType,
            onAllowed: () => history.push(`${Path.MAP}/groups/${groupTarget}/${entityId}`),
            onDenied: () =>
              notification.warn({ message: intl.formatMessage(messages.groupNoAccess) }),
          })
        );
        return;
      }
      if (entityType === EntityType.ROUTE) {
        dispatch(
          checkEntityMentionAccess({
            entityId,
            entityType,
            onAllowed: () => history.push(`${Path.ROUTE}/${RouteSubPath.VIEW}/${entityId}`),
            onDenied: () =>
              notification.warn({ message: intl.formatMessage(messages.routeNoAccess) }),
          })
        );
        return;
      }
      if (entityType === EntityType.TERRITORY) {
        dispatch(
          checkEntityMentionAccess({
            entityId,
            entityType,
            onAllowed: () => history.push(`${Path.MAP}/territories/${entityId}`),
            onDenied: () =>
              notification.warn({ message: intl.formatMessage(messages.territoryNoAccess) }),
          })
        );
      }
    },
    [dispatch, history, intl]
  );
};

export default useEntityMentionNavigation;
